import { Stack } from '@landler/component-library';
import { useTranslation } from 'react-i18next';

import { useOrganizationIndustrialActivityDependencyScores } from '@/pages/shared/disclosure/hooks/useOrganizationIndustrialActivityDependencyScores';
import { useOrganizationIndustrialActivityImpactScores } from '@/pages/shared/disclosure/hooks/useOrganizationIndustrialActivityImpactScores';

import { ActivityRisks } from './ActivityRisks';
import { LinkageCell } from './Cells';
import { ComplianceTargets } from './ComplianceTargets';
import { ScoresHeatmap } from './ScoresHeatmap';
import { StateOfNature } from './StateOfNature';

export const IndustrialActivityDependencies = () => {
  return (
    <Stack className='flex flex-col gap-14' data-testid='industrial-activity-dependencies'>
      <ActivityDependencies />
      <StateOfNature />
      <ActivityRisks category='dependencies' />
      <ComplianceTargets category='impacts' />
    </Stack>
  );
};

export const IndustrialActivityImpacts = () => {
  return (
    <Stack className='flex flex-col gap-14' data-testid='industrial-activity-impacts'>
      <ActivityImpacts />
      <StateOfNature />
      <ActivityRisks category='impacts' />
      <ComplianceTargets category='impacts' />
    </Stack>
  );
};

const ActivityDependencies = () => {
  const { t } = useTranslation();

  const dependencyScores = useOrganizationIndustrialActivityDependencyScores().data;

  const scoresToActivities = dependencyScores.reduce(
    (acc, score) => {
      const dependencyName = score.activity_dependency.dependency;
      if (!acc[`${dependencyName}`]) {
        acc[`${dependencyName}`] = {};
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acc[`${dependencyName}`]![`${score.activity_dependency.activity.activity}`] = score.value;
      return acc;
    },
    {} as Record<string, Record<string, number | undefined>>,
  );
  const data = Object.entries(scoresToActivities).map(([dependencyName, scores]) => ({
    group: `${dependencyName}`,
    ...scores,
  }));

  return <ScoresHeatmap group={t(`shared.disclosure.labels.ecosystemService`)} data={data} factCell={LinkageCell} />;
};

const ActivityImpacts = () => {
  const { t } = useTranslation();

  const impactScores = useOrganizationIndustrialActivityImpactScores().data;

  const scoresToActivities = impactScores.reduce(
    (acc, score) => {
      const impactName = score.activity_impact.impact;
      if (!acc[`${impactName}`]) {
        acc[`${impactName}`] = {};
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acc[`${impactName}`]![`${score.activity_impact.activity.activity}`] = score.value;
      return acc;
    },
    {} as Record<string, Record<string, number | undefined>>,
  );
  const data = Object.entries(scoresToActivities).map(([impactName, scores]) => ({
    group: `${impactName}`,
    ...scores,
  }));
  return <ScoresHeatmap group={t(`shared.disclosure.labels.impactDriver`)} data={data} factCell={LinkageCell} />;
};
